<template>
  <div class="flex lg:flex-row flex-col pt-2 -mx-4">
    <div class="w-full lg:w-4/12 px-4">
      <div class="bg-white rounded shadow-lg mb-6 overflow-hidden">
        <!-- Scope Info -->
        <scope-preview-card :scope-placeholder-url="scopePlaceholderUrl" :current-scope="currentScope" :current-scope-product-variant="currentScopeProductVariant"></scope-preview-card>
      </div>
      <!-- Youtube Video -->
      <div class="hidden lg:block bg-white rounded shadow-lg">
        <div class="p-4 bg-gray-100 border-b flex justify-between">
          <h5 class="uppercase mb-0" v-html='$t("scopePreviewCard.helpTextTitle")'></h5>
        </div>
        <div class="p-4 text-center">
          <!-- Responsive iframe wrapper -->
          <div data-controller="youtube-video" data-youtube-video-id="6g2RROpbnhQ" class="embed-responsive embed-responsive-16by9 mb-3">
            <div class="embed-responsive-item" data-action="click->youtube-video#play" data-target="youtube-video.container">
              <div class="embed-responsive-placeholder">
                <div class="rounded-full w-20 h-20 hover:bg-primary-dark bg-primary flex items-center justify-center">
                  <i class="fas fa-play fa-2x text-white"></i>
                </div>
              </div>
            </div>
          </div>
          <span class="text-sm text text-gray-700" v-html='$t("scopePreviewCard.helpText")'></span>
        </div>
      </div>
    </div>
    <div id="turretCustomizer" class="w-full lg:w-8/12 px-4" data-controller="calculator">
      <div class="rounded shadow-lg bg-white overflow-hidden mb-4">
        <customization-type-section
          :conversion-rate="conversionRate"
          :turret-customization-enabled="turretCustomizationEnabled">
        </customization-type-section>
        <scope-section
          :is-admin-mode="isAdminMode"
          :turret-customization-enabled="turretCustomizationEnabled">
        </scope-section>
        <ammunition-section></ammunition-section>
        <firearm-section></firearm-section>
        <environment-section></environment-section>

        <!-- Notes -->
        <div class="p-4 border-t" v-if="shouldShowNotes">
          <div class="form-group">
            <label class="form-label">{{$t("turretCustomizer.notes")}}</label>
            <textarea name="turret_customization[notes]" v-model="notes" class="form-control" :placeholder="$t('turretCustomizer.notesPlaceholder')"></textarea>
          </div>
        </div>

        <!-- Turret Preview -->
        <div class="p-4 bg-gray-100 z-10 border-t rounded-b">
          <div
            v-on:click.prevent
            v-tippy="{ arrow : true,  animation : 'shift-away'}"
            :content='$t("turretCustomizer.labelNotes")'
            class="relative max-h-xs max-w-full overflow-hidden">
            <turret-label-preview
              :height="Number(valueFromCurrentUnit(turretHeight))"
              :diameter="Number(valueFromCurrentUnit(turretDiameter))"
              :clicks-per-rotation="Number(clicksPerRotation)"
              :number-of-revolutions="Number(numberOfRevolutions)"
              :direction="direction"
              :calculation-results="calculationResults"
              :lines-type="linesType"
              :snap-to-clicks="snapToClicks"
              :revolutions-configuration="revolutionConfigurations">
            </turret-label-preview>

            <!-- Loading Overlay -->
            <div class="absolute inset-0 bg-white" v-if="isCalculationLoading">
              <div class="flex justify-center items-center h-full">
                <i class="fas fa-circle-notch fa-spin text-primary text-2xl"></i>
              </div>
            </div>

            <!-- About Turret Preview -->
            <div class="mt-2 text-xs text-gray-700 text-center">
              {{$t("generic.turretPreview")}}
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="p-4 border-t border-b bg-gray-100">
          <!-- Price -->
          <div class="flex justify-between">
            <div class="uppercase font-bold lg:mr-3">{{$t("generic.subtotal")}}</div>
            <div class="text-right">
              <div class="font-bold">${{ formatPrice(currentScopeProductVariant.price) }} CAD</div>
              <div class="text-gray-700 text-sm font-semibold">
                ${{priceInUsd(currentScopeProductVariant.price)}} USD
                <i class="fas fa-question-circle text-primary"
                  v-tippy="{ arrow : true,  animation : 'shift-away'}"
                  :content=conversionRateText>
                </i>
              </div>
            </div>
          </div>
        </div>
        <div class="p-4 bg-gray-100">
          <div class="flex justify-center">
            <div class="w-full lg:w-1/2">
              <button
                type="submit"
                class="btn btn-primary block w-full"
                :data-disable-with="$t('generic.submitting')">
                  {{ isAdminMode ? $t("generic.submit") : $t("turretCustomizer.addToCart") }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from "vuex"
import scopePreviewCard from "../../scope_preview_card/components/index"
import ScopeSection from "./scope_section"
import FirearmSection from "./firearm_section"
import EnvironmentSection from "./environment_section"
import AmmunitionSection from "./ammunition_section"
import CustomizationTypeSection from "./customization_type_section"
import TurretLabelPreview from "../../turret_preview/components/label_preview"
import LabelPreviewConfigurationMixin from "../../shared/mixins/label_preview_configuration_mixin"
import { toImperial, priceInUsd, formatPrice } from "../module/constants"

const NAVBAR_HEIGHT = 65;

const { mapGetters } = createNamespacedHelpers("turretCustomizer")

export default {
  mixins: [LabelPreviewConfigurationMixin],
  components: {
    scopePreviewCard,
    TurretLabelPreview,
    ScopeSection,
    FirearmSection,
    EnvironmentSection,
    AmmunitionSection,
    CustomizationTypeSection
  },
  data() {
    return {
      notes: ''
    }
  },
  props: {
    scopePlaceholderUrl: String,
    currentScope: Object,
    finalTab: {
      type: String,
      default: "environment"
    },
    submitButtonType: {
      type: String,
      default: "checkout"
    },
    buildMode: {
      type: String,
      default: "user"
    },
    isAuthenticated: {
      type: Boolean,
      default: true
    },
    shouldShowNotes: {
      type: Boolean,
      default: false
    },
    conversionRate: {
      type: Number,
      default: 0.75
    }
  },
  computed: {
    ...mapGetters([
      "isImperial",
      "turretHeight",
      "turretDiameter",
      "permittedCurrentScope",
      "currentScopeProductVariant",
      "currentAmmunition",
      "ammunitionWeight",
      "dragCoefficient",
      "altitude",
      "temperature",
      "initialVelocity",
      "clicksPerRotation",
      "numberOfRevolutions",
      "direction",
      "isCalculationLoading",
      "calculationResults"
    ]),
    ammunitionName() {
      return this.currentAmmunition ? this.currentAmmunition.name : ""
    },
    isAdminMode() {
      return this.buildMode === "admin"
    },
    turretCustomizationEnabled() {
      return this.permittedCurrentScope.turret.allows_customization || this.isAdminMode
    },
    conversionRateText() {
      return `1 CAD = ~${this.conversionRate.toFixed(5)} USD`
    }
  },
  methods: {
    valueFromCurrentUnit(value) {
      return this.isImperial ? value : toImperial("cm", value)
    },
    priceInUsd(price) {
      return priceInUsd(price, this.conversionRate)
    },
    formatPrice(price) {
      return formatPrice(price)
    },
  },
  mounted() {
    this.$store.dispatch("turretCustomizer/fetchCalculation")
  },
}
</script>
